import { useCallback, useEffect, useRef } from 'react'
import useMosTechnologies, { MOS_TECHNOLOGIES_STATUS } from './useMosTechnologies'
import {
  getEffectivePlannedByTechnologyIds, getActivePlannedByTechnologyIds, hasPlannedByTechnologySelection
} from '../utils/plannedJobTabs'

/**
 * Story 193 (MIC-4346): what the "All Jobs" list needs wherever it is shown -- the dialog tab
 * (S31-S37) and the widget's main view (S41/S42).
 *
 * - The catalog of active technologies is read only when the list is `enabled` (visible) AND
 *   something is selected: an empty selection shows the message at once (S35).
 * - `noSelection` (RN5.1/RN5.2): the message replaces the table when nothing counts -- an empty
 *   selection right away, a selection outside the catalog once the catalog has answered.
 * - `dataFetch` waits for the catalog, so while it loads the table shows only its own row spinner and
 *   the list is not asked yet (RN5.4). The selection travels by ref so the fetcher keeps its identity
 *   (the table does not refetch on a new dataFetch, only on forceLoad); a real change of selection
 *   calls `onSelectionChange` instead, for the caller to reload (S37).
 */
const usePlannedByTechnologyFetch = ({ plannedByTechnologyIds = [], enabled = false, fetchPlannedByTechnologyJobs, onSelectionChange = () => {} }) => {
  const selection = getEffectivePlannedByTechnologyIds(plannedByTechnologyIds)
  const selectionKey = selection.join('|')
  const catalog = useMosTechnologies({ enabled: enabled && hasPlannedByTechnologySelection(selection) })

  const noSelection = !hasPlannedByTechnologySelection(selection) || (
    catalog.settled &&
    !hasPlannedByTechnologySelection(getActivePlannedByTechnologyIds(selection, catalog.technologies, catalog.failed))
  )

  const selectionRef = useRef(selection)
  selectionRef.current = selection
  const { whenSettled } = catalog
  const dataFetch = useCallback(async (pagination) => {
    const settled = await whenSettled()
    const technologyIds = getActivePlannedByTechnologyIds(
      selectionRef.current,
      settled.technologies,
      settled.status === MOS_TECHNOLOGIES_STATUS.FAILED
    )
    return fetchPlannedByTechnologyJobs(pagination, technologyIds)
  }, [whenSettled, fetchPlannedByTechnologyJobs])

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange
  const selectionInitRef = useRef(false)
  useEffect(() => {
    if (!selectionInitRef.current) {
      selectionInitRef.current = true
      return
    }
    onSelectionChangeRef.current()
  }, [selectionKey])

  return { noSelection, dataFetch }
}

export default usePlannedByTechnologyFetch
